import { t, Trans } from "@lingui/macro";
import EventEmitter from "events";
import { useEffect, useReducer, useState } from "react";
import { Message } from "../../models/Message";
import { Room } from "../../models/Room";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { Util } from "../../services/Utils/Util";
import { UtilApp } from "../../services/Utils/UtilApp";
import VideoRecorder from "../../services/VideoRecorder";
import MentionTextArea from "./MentionTextArea";
import ProfileImg from "./ProfileImg";
import useDefaultDevices from "./useDefaultDevices";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { Progress } from "../../components/ui/progress";
import { Avatar } from "../../components/ui/avatar";
import { useToast } from "../../components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { X, Mic, Send, Square, Play } from "lucide-react"; // Importing likely utilized icons

const MAX_RECORDING_TIME = 60 * 3;
class Timer {
  static timeoutCounter = 0;
  static maxTimeString = Util.getTimerTimeFormat(MAX_RECORDING_TIME)
  static intervalRef: any = null;
}

export class ScreenRecordModalEmitter {
  private static emitter = new EventEmitter();

  static startRecording(room: Room, parentMessage: Message | undefined) {
    this.emitter.emit("start", room, parentMessage);
  }

  static subscribe(handler: (room: Room, parentMessage: Message | undefined) => void): () => void {
    this.emitter.on("start", handler);
    return () => this.emitter.removeAllListeners();
  }
}

type RecordState = 'hidden' | 'start' | 'selecting-screen' | 'recording' | 'paused' | 'ready-to-upload' | 'uploading';

export const ScreenRecordModal: React.FC = () => {
  const [recorder] = useState(new VideoRecorder());
  const [videoSrc, setVideoSrc] = useState<string>();
  const [videoBlob, setVideoBlob] = useState<Blob>();
  const [state, setState] = useState<RecordState>('hidden')
  const [room, setRoom] = useState<Room>()
  const [parentMessage, setParentMessage] = useState<Message>()
  const [newMessage, setNewMessage] = useState<Message>();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [text, setText] = useState<string>();
  const [useMic, setUseMic] = useState(false);
  const microphoneLabel = Util.getTextInsideParenthesis(useDefaultDevices().audioInputDevice?.label);
  const { toast } = useToast();
  const [showDiscardAlert, setShowDiscardAlert] = useState(false);

  useEffect(() => {
    return ScreenRecordModalEmitter.subscribe((room: Room, parentMessage: Message | undefined) => {
      if (UtilApp.getPlatform() !== 'web') {
        toast({
          title: t`Unavailable`,
          description: t`Screen recording is only available on desktop devices.`,
          variant: "destructive",
        });
      } else {
        setRoom(room);
        setParentMessage(parentMessage);
        setState('start');
      }
    });
  }, []);

  async function startRecording() {
    if (recorder.recorder) {
      toast({
        title: t`Recording in progress`,
        description: t`A recording is already ongoing`,
        variant: "destructive",
      });
    } else if (room) {
      setVideoBlob(undefined);
      setVideoSrc(undefined);
      setState('selecting-screen');
      setText("");

      if (room.id) {
        let newMsg = new Message(room, My.user);
        newMsg.parentMessageId = parentMessage?.parentMessageId ?? null;
        newMsg.replyToMessageId = parentMessage?.id ?? null;
        setNewMessage(newMsg);
      } else {
        setNewMessage(undefined);
        console.error("No roomId passed to ScreenRecording");
      }
      let error = await recorder.startScreenRecording(useMic, stopRecording);
      if (error) {
        setState('hidden');
        toast({
          title: t`Error`,
          description: error,
          variant: "destructive",
        });
      } else {
        Timer.timeoutCounter = 0;
        Timer.intervalRef = setInterval(() => {
          if (Timer.timeoutCounter >= MAX_RECORDING_TIME)
            stopRecording();
          else {
            Timer.timeoutCounter += 1;
            forceUpdate();
          }
        }, 1000);
        setState('recording');
      }
    } else {
      toast({
        title: t`Error`,
        description: "No room selected",
        variant: "destructive",
      });
    }
  }

  async function stopRecording() {
    clearInterval(Timer.intervalRef);
    let blob = await recorder.finish();
    if (blob) {
      setVideoBlob(blob);
      setVideoSrc(URL.createObjectURL(blob));
      setState('ready-to-upload');
    } else {
      toast({
        description: t`Oops. No recording was found`,
      });
      setState('hidden');
    }
  }

  async function save() {
    if (videoBlob && newMessage && room) {
      setState('uploading');
      let ext = getFileExt(videoBlob.type);
      Fire.uploadFile(videoBlob, `/rooms/${room.id}/videos/video-${new Date().getTime()}.${ext}`, (url) => {
        newMessage.videoUrl = url;
        newMessage.text = text;
        Fire.msg.saveMessageAndNotify(newMessage);
        setState('hidden');
        setText('');
      }, percent => setUploadProgress(percent));
    } else {
      console.error("No blob, room or new message", newMessage)
    }
  }

  function attemptClose() {
    if (state === 'start') {
      setState('hidden');
    } else if (state !== 'uploading') {
      setShowDiscardAlert(true);
    }
  }

  function confirmDiscard() {
    recorder.resetAll();
    setState('hidden');
    setShowDiscardAlert(false);
  }

  function getFileExt(mimeType: string): 'mp4' | 'webm' | 'mkv' | 'unknown' {
    if (mimeType.includes('mp4'))
      return 'mp4';
    else if (mimeType.includes('webm'))
      return 'webm'
    else if (mimeType.includes('matroska'))
      return 'mkv'
    else
      return 'unknown'
  }

  function ParentMessageHeader() {
    if (parentMessage) {
      return (
        <div className="flex items-center gap-2 p-3 bg-secondary/50 rounded-md mb-4 text-sm">
          <img src="assets/icon/ic-arrow-right-white-24.svg" className="w-4 h-4 opacity-50 filter invert dark:invert-0" alt="arrow" />
          <Avatar className="w-5 h-5">
            <ProfileImg url={parentMessage.user.photoUrl} className="w-full h-full object-cover" />
          </Avatar>
          <span className="truncate text-foreground font-medium">
            {parentMessage.user.name}
            {parentMessage.text && (
              <>: <span className="font-normal" dangerouslySetInnerHTML={{ __html: UtilApp.getTextAsHTML(parentMessage.text, My.user.id) }} /></>
            )}
          </span>
        </div>
      );
    } else {
      return null;
    }
  }

  if (!room) return null;

  return (
    <>
      <Dialog open={state !== 'hidden'} onOpenChange={(open) => { if (!open) attemptClose(); }}>
        <DialogContent
          className="sm:max-w-xl max-h-[90vh] overflow-y-auto [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b">
            <Button
              variant="ghost"
              size="icon"
              onClick={attemptClose}
              disabled={state === 'uploading'}
            >
              <img src="assets/icon/ic-cancel-black-24.svg" className="w-6 h-6 dark:invert" alt="close" />
            </Button>
            <DialogTitle className="text-lg font-semibold"><Trans>Screen Recording</Trans></DialogTitle>
            <div className="w-10">
              {state === 'ready-to-upload' && (
                <Button onClick={save} variant="ghost" className="text-primary font-semibold p-0 h-auto hover:bg-transparent">
                  <Trans>Submit</Trans>
                </Button>
              )}
            </div>
          </DialogHeader>

          <div className="py-4">
            <ParentMessageHeader />

            {state === 'ready-to-upload' ? (
              <div className="space-y-4">
                <h5 className="text-center font-medium"><Trans>Submit the video to #{Room.getRoomName(room, My.user.id)}?</Trans></h5>
                {videoSrc && (
                  <div className="rounded-lg overflow-hidden bg-black aspect-video flex items-center justify-center">
                    <video controls className="max-w-full max-h-[400px]" src={videoSrc} />
                  </div>
                )}
                <div className="p-1">
                  <MentionTextArea
                    text={text!}
                    placeholder={t`Mention with '@'` + ' ' + t`. Send with ctrl+enter.`}
                    suggestions={room.users.map(u => ({ id: u.id, display: u.name }))}
                    onChange={(value) => { setText(value) }}
                    onSubmit={save}
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[200px] text-center space-y-6">
                {state === 'start' && (
                  <>
                    <h5 className="font-medium text-lg"><Trans>Create a screen recording and post</Trans></h5>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="useMic"
                        checked={useMic}
                        onCheckedChange={(c) => setUseMic(!!c)}
                      />
                      <Label htmlFor="useMic" className="cursor-pointer">
                        <Trans>Use mic</Trans> ({microphoneLabel})
                      </Label>
                    </div>
                    <Button onClick={startRecording} size="lg" className="w-full max-w-xs">
                      <Trans>Start Screen Recording</Trans>
                    </Button>
                  </>
                )}

                {state === 'recording' && (
                  <>
                    <h4 className="text-primary font-semibold animate-pulse"><Trans>Recording screen</Trans></h4>
                    <p className="text-muted-foreground text-sm">#{Room.getRoomName(room, My.user.id)}</p>
                    <h2 className="text-4xl font-mono font-bold my-4">
                      {Util.getTimerTimeFormat(Timer.timeoutCounter)}
                      <span className="text-muted-foreground text-xl">/{Timer.maxTimeString}</span>
                    </h2>
                    <Button onClick={stopRecording} variant="destructive" size="lg">
                      <Square className="w-4 h-4 mr-2" />
                      <Trans>Stop Recording</Trans>
                    </Button>
                  </>
                )}

                {state === 'uploading' && (
                  <div className="w-full max-w-sm space-y-4">
                    <h5 className="font-medium">{uploadProgress < 100 ? t`Uploading video...` : t`Just a moment...`}</h5>
                    <Progress value={uploadProgress} className="h-2" />
                    <h3 className="text-right text-sm font-mono">{uploadProgress}%</h3>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDiscardAlert} onOpenChange={setShowDiscardAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><Trans>Discard Recording?</Trans></AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>Are you sure you want to throw away this video?</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><Trans>No</Trans></AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscard} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              <Trans>Yes, throw away</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default ScreenRecordModal;